// src/pages/budgeting/boq/TowerSiteBOQCmeForm.jsx
import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  ChevronDown,
  ChevronRight,
  Package,
  Info,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import ItemPickerModal from '../../../components/boq/ItemPickerModal';
import towerSiteBOQService from '../../../services/towerSiteBOQService';
import budgetCategoryService from '../../../services/budgetCategoryService';

/**
 * Tower Site BOQ CME Form - WITH TREE STRUCTURE
 * 
 * UPDATED: Now displays full category hierarchy with color coding
 * - Level 0 (A-J): Purple headers, group only
 * - Level 1 (A.1, A.2, A.3): Blue headers, can be group or leaf
 * - Level 2 (A.2.a-g): Green headers, leaf only
 * 
 * Volume-only entry (no pricing calculations)
 * Pricing will be done in Tower Budgeting (RAP) phase
 * 
 * IMPORTANT: Can only be created after SITAC BOQ exists
 */
const TowerSiteBOQCmeForm = ({ site, onBack, onSave }) => {
  // ============= STATE MANAGEMENT =============
  
  // Categories data (tree structure)
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  
  // BOQ data per category: { categoryId: { items: [...], remarks: '', deletedItemIds: [...] } }
  const [boqData, setBOQData] = useState({});
  
  // UI state - track expanded categories by ID
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // SITAC validation
  const [hasSitacBOQ, setHasSitacBOQ] = useState(false);
  const [isCheckingSitac, setIsCheckingSitac] = useState(true);
  
  // Item Picker Modal
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [currentCategoryId, setCurrentCategoryId] = useState(null);

  // ============= LIFECYCLE =============
  
  useEffect(() => {
    console.log('📍 TOWER SITE DATA RECEIVED:', site);
    
    const loadData = async () => {
      // Check SITAC BOQ
      setIsCheckingSitac(true);
      try {
        const exists = await towerSiteBOQService.checkSitacBOQExists(site.id);
        setHasSitacBOQ(exists);
        
        if (!exists) {
          toast.error('SITAC BOQ must be created first before CME BOQ', {
            duration: 5000
          });
        }
      } catch (error) {
        console.error('❌ Error checking SITAC BOQ:', error);
        setHasSitacBOQ(false);
      } finally {
        setIsCheckingSitac(false);
      }
      
      // Load categories with TREE STRUCTURE
      setIsLoadingCategories(true);
      try {
        console.log('📂 Loading CME budget categories tree...');
        const categoryTree = await budgetCategoryService.getRootCategoriesTree('TOWER_CME');
        console.log('✅ Received category tree:', categoryTree);
        
        if (!categoryTree || categoryTree.length === 0) {
          console.warn('⚠️ No CME categories found!');
          toast.error('No CME budget categories available');
        } else {
          setCategories(categoryTree);
          
          // Auto-expand all categories by default
          const allCategoryIds = new Set();
          const collectIds = (cats) => {
            cats.forEach(cat => {
              allCategoryIds.add(cat.id);
              if (cat.children && cat.children.length > 0) {
                collectIds(cat.children);
              }
            });
          };
          collectIds(categoryTree);
          setExpandedCategories(allCategoryIds);
          
          console.log('✅ Loaded CME category tree with', categoryTree.length, 'root categories');
        }
      } catch (error) {
        console.error('❌ Error loading categories:', error);
        toast.error('Failed to load budget categories: ' + (error.message || 'Unknown error'));
      } finally {
        setIsLoadingCategories(false);
      }
      
      // Load existing BOQ
      setIsLoading(true);
      try {
        const boqs = await towerSiteBOQService.getCmeBOQsByTowerSiteId(site.id);
        
        if (boqs && boqs.length > 0) {
          // Transform to { categoryId: { items: [...], remarks: '', deletedItemIds: [] } }
          const transformed = {};
          boqs.forEach(boq => {
            transformed[boq.budgetCategoryId] = {
              boqId: boq.id,
              items: boq.items || [],
              remarks: boq.remarks || '',
              deletedItemIds: [] // Initialize empty array for tracking deletions
            };
          });
          
          setBOQData(transformed);
          console.log('✅ Loaded existing CME BOQ:', transformed);
          toast.success('Existing CME BOQ loaded');
        }
      } catch (error) {
        console.error('❌ Error loading BOQ:', error);
        // Don't show error toast - BOQ might not exist yet
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site.id]);

  // ============= UI HANDLERS =============
  
  /**
   * Toggle category expansion
   */
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };
  
  /**
   * Open item picker for a category
   */
  const handleAddItem = (categoryId) => {
    if (!hasSitacBOQ) {
      toast.error('Please create SITAC BOQ first');
      return;
    }
    
    setCurrentCategoryId(categoryId);
    setShowItemPicker(true);
  };
  
  /**
   * Handle item selection from picker
   */
  const handleItemSelect = (item) => {
    if (!currentCategoryId) return;
    
    // DEBUG: Log full item structure to see what fields are available
    console.log('🎯 CME FORM - Item received from picker:', item);
    console.log('📋 Available fields:', Object.keys(item));
    
    // Create new BOQ item (volume-only, no pricing)
    const newItem = {
      tempId: `temp-${Date.now()}`,
      itemId: item.id,
      itemNumber: item.itemNumber,
      itemCode: item.itemCode,
      itemDescription: item.itemDescription || item.itemName || item.description || 'NO DESCRIPTION',
      itemType: item.itemType,
      unit: item.unit,
      volume: 1, // Default volume
      remarks: '',
      sequenceOrder: (boqData[currentCategoryId]?.items?.length || 0) + 1
    };
    
    console.log('✅ CME FORM - Created BOQ item:', newItem);
    
    // Add to BOQ data
    setBOQData(prev => ({
      ...prev,
      [currentCategoryId]: {
        ...prev[currentCategoryId],
        items: [...(prev[currentCategoryId]?.items || []), newItem]
      }
    }));
    
    setShowItemPicker(false);
    toast.success('Item added to BOQ');
  };
  
  /**
   * Handle item field change (volume, remarks)
   */
  const handleItemFieldChange = (categoryId, itemId, field, value) => {
    setBOQData(prev => {
      const categoryData = prev[categoryId] || { items: [] };
      const items = categoryData.items.map(item => {
        if ((item.tempId || item.id) === itemId) {
          return { ...item, [field]: value };
        }
        return item;
      });
      
      return {
        ...prev,
        [categoryId]: {
          ...categoryData,
          items
        }
      };
    });
  };
  
  /**
   * Delete item from BOQ
   */
  const handleDeleteItem = (categoryId, itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    console.log('======================================');
    console.log('🗑️ DELETE ITEM CALLED');
    console.log('Category ID:', categoryId);
    console.log('Item ID to delete:', itemId);
    console.log('Item ID type:', typeof itemId);
    console.log('Current items BEFORE delete:', boqData[categoryId]?.items);
    
    // Log each item's ID for comparison
    boqData[categoryId]?.items.forEach((item, index) => {
        console.log(`  Item ${index}: id=${item.id}, tempId=${item.tempId}, match=${(item.tempId || item.id) === itemId}`);
    });
    console.log('======================================');    
    
    setBOQData(prev => {
      const categoryData = prev[categoryId] || { items: [], deletedItemIds: [] };
      
      // Find the item to check if it has a real ID (from backend)
      const itemToDelete = categoryData.items.find(item => 
        (item.tempId || item.id) === itemId
      );

      console.log('✅ Item found to delete:', itemToDelete);
      
      // Filter out the deleted item
      const items = categoryData.items.filter(item => 
        (item.tempId || item.id) !== itemId
      );
      
        console.log('✅ Items AFTER filter (should be less):', items);
        console.log('   Count before:', categoryData.items.length);
        console.log('   Count after:', items.length);

      // Track deleted item ID if it's a real item (not temp)
      // Track deleted item ID if it's a real item (not temp)
// NOTE: Currently not used, backend uses replace pattern (clear all + add new)
// Keep this for potential future implementation of incremental updates
      let deletedItemIds = [...(categoryData.deletedItemIds || [])];
      if (itemToDelete && itemToDelete.id && !itemToDelete.tempId) {
        deletedItemIds.push(itemToDelete.id);
      }

        const newData = {
        ...prev,
        [categoryId]: {
            ...categoryData,
            items,
            deletedItemIds: [...(categoryData.deletedItemIds || []), ...(itemToDelete?.id ? [itemToDelete.id] : [])]
        }
        };
        
        console.log('✅ New BOQ data state:', newData[categoryId]);
        console.log('======================================');
        
        return newData;
    });
    
    toast.success('Item deleted from BOQ');
  };
  
  /**
   * Handle category remarks change
   */
  const handleCategoryRemarksChange = (categoryId, remarks) => {
    setBOQData(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        remarks
      }
    }));
  };
  
  /**
   * Save BOQ to backend
   */
    const handleSave = async () => {
    if (!hasSitacBOQ) {
        toast.error('Cannot save CME BOQ. Please create SITAC BOQ first.');
        return;
    }
    
    setIsSaving(true);
    
    try {
        // Collect all leaf categories (can_add_direct=true)
        const leafCategories = [];
        const collectLeafCategories = (cats) => {
        cats.forEach(cat => {
            if (cat.canAddDirect) {
            leafCategories.push(cat);
            }
            if (cat.children && cat.children.length > 0) {
            collectLeafCategories(cat.children);
            }
        });
        };
        collectLeafCategories(categories);
        
        // ✅ NEW LOGIC: Save all categories that have been touched
        // This includes:
        // 1. Categories with items (new or existing)
        // 2. Categories that HAD a BOQ before (even if now empty - to clear backend)
        const categoriesToSave = leafCategories.filter(cat => {
        const categoryData = boqData[cat.id];
        
        return categoryData && (
            (categoryData.items && categoryData.items.length > 0) ||
            categoryData.boqId
        );
        });
        
        console.log('======================================');
        console.log('💾 SAVING BOQ');
        console.log('Total leaf categories:', leafCategories.length);
        console.log('Categories to save:', categoriesToSave.length);
        categoriesToSave.forEach(cat => {
        const itemCount = boqData[cat.id]?.items?.length || 0;
        const boqId = boqData[cat.id]?.boqId || 'NEW';
        console.log(`  - ${cat.categoryCode} (BOQ ID: ${boqId}): ${itemCount} items`);
        });
        console.log('======================================');
        
        // ✅ UPDATED VALIDATION: Check if any category has been touched
        if (categoriesToSave.length === 0) {
        toast.error('Please add at least one item to any category');
        setIsSaving(false);
        return;
        }
        
        // Save each category (including empty ones that need clearing)
        const savePromises = categoriesToSave.map(category => {
        const categoryData = boqData[category.id];
        
        // Prepare request
        const request = {
            towerSiteId: site.id,
            budgetCategoryId: category.id,
            boqType: 'CME',
            remarks: categoryData.remarks || '',
            items: (categoryData.items || []).map((item, index) => ({
            itemId: item.itemId,
            volume: parseFloat(item.volume) || 0,
            remarks: item.remarks || '',
            sequenceOrder: index + 1
            }))
        };
        
        console.log('💾 Saving category', category.categoryCode, ':', request);
        
        return towerSiteBOQService.saveBOQ(request);
        });
        
        await Promise.all(savePromises);
        
        toast.success('CME BOQ saved successfully!');
        
        // Callback to parent
        if (onSave) {
        onSave(site.id);
        }
        
    } catch (error) {
        console.error('❌ Error saving BOQ:', error);
        toast.error(error.response?.data?.message || 'Failed to save BOQ');
    } finally {
        setIsSaving(false);
    }
    };

  // ============= RENDER HELPERS =============
  
  /**
   * Get background color based on level
   */
  const getLevelColorClass = (level) => {
    switch (level) {
      case 0:
        return 'bg-gradient-to-r from-purple-600 to-purple-700';
      case 1:
        return 'bg-gradient-to-r from-blue-600 to-blue-700';
      case 2:
        return 'bg-gradient-to-r from-green-600 to-green-700';
      default:
        return 'bg-gradient-to-r from-gray-600 to-gray-700';
    }
  };
  
  /**
   * Get hover color based on level
   */
  const getLevelHoverClass = (level) => {
    switch (level) {
      case 0:
        return 'hover:bg-purple-500';
      case 1:
        return 'hover:bg-blue-500';
      case 2:
        return 'hover:bg-green-500';
      default:
        return 'hover:bg-gray-500';
    }
  };
  
  /**
   * Get button color based on level
   */
  const getLevelButtonClass = (level) => {
    switch (level) {
      case 0:
        return 'bg-white text-purple-700 hover:bg-purple-50';
      case 1:
        return 'bg-white text-blue-700 hover:bg-blue-50';
      case 2:
        return 'bg-white text-green-700 hover:bg-green-50';
      default:
        return 'bg-white text-gray-700 hover:bg-gray-50';
    }
  };
  
  /**
   * Get level badge color
   */
  const getLevelBadgeClass = (level) => {
    switch (level) {
      case 0:
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 1:
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 2:
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  
  /**
   * Render single BOQ item row
   */
  const renderItemRow = (categoryId, item, index) => {
    return (
      <tr key={item.tempId || item.id} className="border-b border-gray-200 hover:bg-gray-50">
        {/* No */}
        <td className="px-4 py-3 text-center text-sm text-gray-700 font-medium">
          {index + 1}
        </td>
        
        {/* Item Info */}
        <td className="px-4 py-3">
          <div className="flex items-start gap-2">
            <Package className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-600">
                  {item.itemCode || item.itemNumber}
                </span>
                <span className="text-sm font-semibold text-gray-900 truncate">
                  {item.itemDescription}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Type: {item.itemType}
              </div>
            </div>
          </div>
        </td>
        
        {/* Unit */}
        <td className="px-4 py-3 text-sm text-gray-700 text-center">
          {item.unit}
        </td>
        
        {/* Volume */}
        <td className="px-4 py-3">
          <input
            type="number"
            value={item.volume}
            onChange={(e) => handleItemFieldChange(categoryId, item.tempId || item.id, 'volume', e.target.value)}
            min="0"
            step="0.01"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </td>
        
        {/* Remarks */}
        <td className="px-4 py-3">
          <input
            type="text"
            value={item.remarks || ''}
            onChange={(e) => handleItemFieldChange(categoryId, item.tempId || item.id, 'remarks', e.target.value)}
            placeholder="Optional notes..."
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </td>
        
        {/* Actions */}
        <td className="px-4 py-3 text-center">
          <button
            onClick={() => handleDeleteItem(categoryId, item.tempId || item.id)}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete Item"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </td>
      </tr>
    );
  };
  
  /**
   * Render category recursively (tree structure)
   */
  const renderCategory = (category, level = 0) => {
    const isExpanded = expandedCategories.has(category.id);
    const categoryItems = boqData[category.id]?.items || [];
    const hasChildren = category.children && category.children.length > 0;
    const isLeaf = category.canAddDirect && !hasChildren;
    
    // Indentation based on level
    const indentClass = level === 0 ? '' : level === 1 ? 'ml-8' : 'ml-16';
    
    return (
      <div key={category.id} className={`${indentClass} mb-4`}>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          {/* Category Header */}
          <div className={`${getLevelColorClass(level)} p-4`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                {/* Expand/Collapse button - only if has children or has items */}
                {(hasChildren || isLeaf) && (
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className={`p-1 ${getLevelHoverClass(level)} rounded transition-colors`}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-white" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-white" />
                    )}
                  </button>
                )}
                
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-white">
                      {category.categoryCode} - {category.categoryName}
                    </h3>
                    
                    {/* Level Badge */}
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getLevelBadgeClass(level)}`}>
                      L{level}
                    </span>
                    
                    {/* Type Badge */}
                    {category.isGroup && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 rounded text-xs font-semibold">
                        Group
                      </span>
                    )}
                    {isLeaf && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-800 border border-green-300 rounded text-xs font-semibold">
                        Leaf
                      </span>
                    )}
                  </div>
                  
                  {/* Subtitle */}
                  {category.subtitle && (
                    <p className="text-sm text-white opacity-90 mt-1">
                      {category.subtitle}
                    </p>
                  )}
                  
                  {/* Item count for leaf categories */}
                  {isLeaf && (
                    <p className="text-sm text-white opacity-90 mt-1">
                      {categoryItems.length} item{categoryItems.length !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Add Item button - only for leaf categories */}
              {isLeaf && (
                <button
                  onClick={() => handleAddItem(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 ${getLevelButtonClass(level)} rounded-lg transition-colors shadow-md`}
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </button>
              )}
            </div>
          </div>

          {/* Category Content - only for leaf categories */}
          {isLeaf && isExpanded && (
            <div className="p-6">
              {/* Category Remarks */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Category Remarks (Optional)
                </label>
                <textarea
                  value={boqData[category.id]?.remarks || ''}
                  onChange={(e) => handleCategoryRemarksChange(category.id, e.target.value)}
                  placeholder="Enter any notes for this category..."
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Items Table */}
              {categoryItems.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">No items added yet</p>
                  <p className="text-sm text-gray-500 mt-1">Click "Add Item" to add items to this category</p>
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">No</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Item</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Unit</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Volume</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Remarks</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {categoryItems.map((item, index) => renderItemRow(category.id, item, index))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Render Children Recursively */}
        {hasChildren && isExpanded && (
          <div className="mt-4">
            {category.children.map(child => renderCategory(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // ============= MAIN RENDER =============
  
  if (isCheckingSitac || isLoadingCategories || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading CME BOQ...</p>
        </div>
      </div>
    );
  }

  // Show error if SITAC BOQ doesn't exist
  if (!hasSitacBOQ) {
    return (
      <div className="space-y-6">
        {/* Page Header */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack} 
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">BOQ CME - Construction</h1>
              <p className="text-gray-600 mt-1">{site.siteName} ({site.siteId})</p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-900 mb-2">SITAC BOQ Required</h2>
          <p className="text-red-800 mb-4">
            CME BOQ can only be created after SITAC BOQ exists.
          </p>
          <p className="text-sm text-red-700">
            Please go back and create SITAC BOQ first, then return to create CME BOQ.
          </p>
          <button
            onClick={onBack}
            className="mt-6 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack} 
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">BOQ CME - Civil, Mechanical, Electrical</h1>
              <p className="text-gray-600 mt-1">{site.siteName} ({site.siteId})</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-5 h-5" />
            {isSaving ? 'Saving...' : 'Save CME BOQ'}
          </button>
        </div>
      </div>

      {/* SITAC Status - Success */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-green-900 mb-1">SITAC BOQ Exists ✓</h3>
            <p className="text-sm text-green-800">
              SITAC BOQ has been created. You can now proceed with CME BOQ.
            </p>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-1">CME BOQ - Volume Only (Tree Structure)</h3>
            <p className="text-sm text-blue-800 mb-2">
              Enter volume/quantity for each item. Pricing will be calculated later in the Tower Budgeting (RAP) phase.
            </p>
            <div className="text-sm text-blue-800">
              <strong>Color Coding:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li><span className="font-semibold text-purple-700">Purple</span> - Level 0 (Root categories: A-J)</li>
                <li><span className="font-semibold text-blue-700">Blue</span> - Level 1 (Sub-categories: A.1, A.2, A.3)</li>
                <li><span className="font-semibold text-green-700">Green</span> - Level 2 (Leaf categories: A.2.a, A.2.b, etc.)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Tree */}
      {categories.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
          <p className="text-yellow-800 font-medium">No CME categories available</p>
          <p className="text-sm text-yellow-700 mt-1">Please contact administrator to set up budget categories</p>
        </div>
      ) : (
        <div className="space-y-4">
          {categories.map(category => renderCategory(category, 0))}
        </div>
      )}

      {/* Item Picker Modal */}
      <ItemPickerModal
        isOpen={showItemPicker}
        onSelect={handleItemSelect}
        onClose={() => setShowItemPicker(false)}
      />
    </div>
  );
};

export default TowerSiteBOQCmeForm;